import React, { Fragment } from 'react';
import Link from 'shared/SlashLink';
import usePageSeo from '../../hooks/usePageSeo';
import Header from '../../components/header/Header';
import PageTitle from '../../components/pagetitle/PageTitle';
import Scrollbar from '../../components/scrollbar/scrollbar';
import Footer from '../../components/footer/Footer';
import CtaSection from '../../components/CtaSection/CtaSection';

const ClickHandler = () => window.scrollTo(10, 0);

const PILLARS = [
    {
        icon: 'fa-server',
        title: 'Systems & Network Administration',
        text: 'Day-to-day administration of servers, endpoints, identity, and network — keeping the lights on so your team focuses on the work, not the tooling underneath it.',
    },
    {
        icon: 'fa-shield-halved',
        title: 'Cyber Security',
        text: 'Posture assessments, endpoint protection (EDR), email security, MFA rollouts, and monitoring — real defense for businesses without enterprise overhead.',
    },
    {
        icon: 'fa-cloud',
        title: 'Cloud & Infrastructure',
        text: 'Right-sized infrastructure — on-prem, hybrid, or cloud — with backup, disaster recovery, and migration support that scales with your business.',
    },
    {
        icon: 'fa-headset',
        title: 'Managed Services',
        text: 'A predictable monthly plan with helpdesk, proactive monitoring, patching, and one team accountable for the whole stack. No surprise invoices.',
    },
];

const TIERS = [
    {
        name: 'Essential',
        price: 125,
        tagline: 'Helpdesk + monitoring to keep things running.',
        featured: false,
        features: [
            'Business-hours helpdesk & remote support',
            'Remote monitoring & management (RMM)',
            'Patch & update management',
            'Endpoint protection (antivirus / EDR)',
            'Email security & spam filtering',
            'Asset inventory & documentation',
        ],
    },
    {
        name: 'Professional',
        price: 185,
        tagline: 'Most popular — security, cloud, and a real IT partner.',
        featured: true,
        features: [
            'Everything in Essential',
            'Microsoft 365 / Google Workspace management',
            'Cloud backup & disaster recovery',
            'Managed detection & response (MDR)',
            'Network & server administration',
            'Vendor & license management',
            'Quarterly technology reviews',
        ],
    },
    {
        name: 'Complete',
        price: 275,
        tagline: 'Compliance, 24/7 SOC, and strategic IT leadership.',
        featured: false,
        features: [
            'Everything in Professional',
            '24/7 SOC monitoring & response',
            'Compliance support (HIPAA / SOC 2)',
            'Virtual CIO (vCIO) strategic roadmap',
            'Priority response SLA',
            'Security awareness training',
        ],
    },
];

const COVERED = [
    'Workstations & laptops', 'Servers (physical & virtual)', 'Firewalls & switches',
    'Microsoft 365 / Google Workspace', 'Identity & access (MFA, SSO)', 'Backup & recovery',
    'Email security', 'Endpoint detection & response', 'Patch management',
    'Cloud (Azure / AWS / hybrid)', 'VoIP & collaboration', 'Vendor coordination',
];

const ItServicesPage = () => {
    usePageSeo({
        title: 'Managed IT Services — Systems, Security & Cloud',
        description: 'Managed IT, network administration, cyber security, and cloud on predictable per-user pricing — Essential, Professional, and Complete tiers from $125/user/mo.',
        keywords: 'managed IT services, IT services, cyber security, systems and network administration, managed services pricing, per user IT, helpdesk, MDR, vCIO, cloud infrastructure, BUILDLAB',
    });

    return (
        <Fragment>
            <Header />
            <main id="main-content" className="page_content">
                <PageTitle pageTitle={'Managed IT Services in California'} pagesub={'Systems · Security · Cloud'} pageTop={'Business'} />

                {/* Intro */}
                <section className="section_space bg-light">
                    <div className="container">
                        <div className="heading_block text-center">
                            <div className="heading_focus_text">
                                One team for
                                <span className="badge bg-secondary text-white">all of IT</span>
                            </div>
                            <h2 className="heading_text mb-3">Systems, security, cloud, and support — managed as one</h2>
                            <p className="heading_description mb-0" style={{ maxWidth: 760, margin: '0 auto' }}>
                                We consolidated systems &amp; network administration, cyber security, cloud &amp; infrastructure,
                                and managed services into a single accountable practice. You get one partner who understands how
                                your whole environment fits together — and designs from that eagle-eye view down.
                            </p>
                        </div>

                        <div className="row">
                            {PILLARS.map((p, i) => (
                                <div className="col-lg-3 col-md-6 mt-4" key={i}>
                                    <div style={{ background: '#fff', border: '1px solid var(--bs-border-color)', borderRadius: 12, padding: '28px 24px', height: '100%' }}>
                                        <span style={{
                                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                            width: 54, height: 54, borderRadius: 12, marginBottom: 16,
                                            background: 'var(--bs-primary-bg-subtle)', color: 'var(--bs-primary)', fontSize: 22,
                                        }}>
                                            <i className={`fa-solid ${p.icon}`} aria-hidden="true"></i>
                                        </span>
                                        <h3 style={{ fontSize: '1.15rem', color: 'var(--bs-dark)', marginBottom: 8 }}>{p.title}</h3>
                                        <p className="mb-0" style={{ fontSize: '.92rem' }}>{p.text}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Managed IT pricing */}
                <section className="section_space">
                    <div className="container">
                        <div className="heading_block text-center">
                            <div className="heading_focus_text">
                                Managed IT
                                <span className="badge bg-secondary text-white">Pricing</span>
                            </div>
                            <h2 className="heading_text mb-3">Predictable per-user plans</h2>
                            <p className="heading_description mb-3" style={{ maxWidth: 720, margin: '0 auto' }}>
                                Flat monthly pricing per user — no surprise invoices. Most clients land on Professional.
                                Plans scale with your team and can be tailored to your stack.
                            </p>
                            <Link to="/it-cost-calculator" className="btn btn-outline-primary">
                                <span className="btn_label" data-text="Estimate your monthly cost">Estimate your monthly cost</span>
                                <span className="btn_icon"><i className="fa-solid fa-calculator" aria-hidden="true"></i></span>
                            </Link>
                        </div>

                        <div className="row justify-content-center">
                            {TIERS.map((t, i) => (
                                <div className="col-lg-4 col-md-6 mt-4" key={i}>
                                    <div style={{
                                        background: t.featured ? 'var(--bs-primary)' : '#fff',
                                        color: t.featured ? '#fff' : 'inherit',
                                        border: t.featured ? 'none' : '1px solid var(--bs-border-color)',
                                        borderRadius: 16,
                                        padding: '32px 28px',
                                        height: '100%',
                                        position: 'relative',
                                        boxShadow: t.featured ? '0 16px 40px rgba(11,19,43,0.25)' : '0 4px 14px rgba(0,0,0,0.05)',
                                    }}>
                                        {t.featured && (
                                            <span style={{
                                                position: 'absolute', top: 18, right: 18,
                                                background: 'var(--bs-secondary)', color: '#fff',
                                                fontSize: '.7rem', fontWeight: 700, padding: '4px 12px', borderRadius: 20,
                                                textTransform: 'uppercase', letterSpacing: '.06em',
                                            }}>Most Popular</span>
                                        )}
                                        <h3 style={{ color: t.featured ? '#fff' : 'var(--bs-dark)', marginBottom: 6 }}>{t.name}</h3>
                                        <p style={{ fontSize: '.88rem', opacity: t.featured ? 0.85 : 0.7, marginBottom: 18 }}>{t.tagline}</p>
                                        <div style={{ marginBottom: 22 }}>
                                            <span style={{ fontSize: '2.6rem', fontWeight: 800, lineHeight: 1 }}>${t.price}</span>
                                            <span style={{ fontSize: '.9rem', opacity: 0.8 }}> / user / month</span>
                                        </div>
                                        <ul className="unordered_list_block" style={{ marginBottom: 26 }}>
                                            {t.features.map((f, fi) => (
                                                <li key={fi} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10, fontSize: '.9rem' }}>
                                                    <i className="fa-solid fa-check" aria-hidden="true" style={{ marginTop: 3, color: t.featured ? '#fff' : 'var(--bs-primary)' }}></i>
                                                    <span>{f}</span>
                                                </li>
                                            ))}
                                        </ul>
                                        <Link
                                            onClick={ClickHandler}
                                            to="/contact"
                                            className="btn"
                                            style={{
                                                width: '100%', textAlign: 'center',
                                                background: t.featured ? '#fff' : 'var(--bs-primary)',
                                                color: t.featured ? 'var(--bs-primary)' : '#fff',
                                                fontWeight: 600, borderRadius: 8, padding: '12px 0',
                                            }}
                                        >
                                            Get a Quote
                                        </Link>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <p className="text-center mt-4 mb-0" style={{ fontSize: '.85rem', color: 'var(--bs-body-color)' }}>
                            Per-server and per-network-device add-ons available. Compliance overlays (HIPAA, SOC 2, CMMC) priced
                            separately. Minimum seat counts may apply. Pricing shown is a starting point — final quotes are scoped to your environment.
                        </p>
                    </div>
                </section>

                {/* What's covered */}
                <section className="section_space bg-light">
                    <div className="container">
                        <div className="heading_block text-center">
                            <div className="heading_focus_text">
                                What we
                                <span className="badge bg-secondary text-white">cover</span>
                            </div>
                            <h2 className="heading_text mb-0">Everything in your environment, accounted for</h2>
                        </div>
                        <div className="row">
                            {COVERED.map((c, i) => (
                                <div className="col-lg-4 col-md-6" key={i}>
                                    <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '12px 0', borderBottom: '1px solid var(--bs-border-color)' }}>
                                        <i className="fa-solid fa-circle-check" aria-hidden="true" style={{ color: 'var(--bs-primary)' }}></i>
                                        <span style={{ fontWeight: 500, color: 'var(--bs-dark)' }}>{c}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <CtaSection />
            </main>
            <Footer />
            <Scrollbar />
        </Fragment>
    );
};

export default ItServicesPage;
